import { useId } from 'react'
import TextField from '../../components/TextField'

/**
 * A labelled field for the auth screens.
 *
 * These used to be floating-label inputs, which on an Arabic form read as a
 * placeholder that happens to move: at rest the label sits INSIDE the box, so a
 * filled form and an empty one look alike at a glance, and a required field
 * cannot be marked without the mark landing inside the input. A label above the
 * control is what every form in the admin already does — the same TextField,
 * the same shell, one less trick.
 *
 * @param {import('react').ElementType} [icon]  leading glyph inside the control
 * @param {import('react').ReactNode} [trailing]
 * @param {string} [error]  message shown under the field, and marks it invalid
 * @param {boolean} [invalid]  marks the control without printing a message —
 *   for a form that reports its failure once, in a banner, rather than per field
 */
export default function AuthField({
  id,
  label,
  icon,
  trailing,
  error,
  invalid,
  hint,
  children,
  ...rest
}) {
  const generated = useId()
  const fieldId = id ?? generated

  return (
    <div className="auth__field">
      <label className="auth__label" htmlFor={fieldId}>
        <bdi>{label}</bdi>
      </label>

      {children ? (
        children(fieldId)
      ) : (
        <TextField
          id={fieldId}
          icon={icon}
          trailing={trailing}
          invalid={Boolean(error) || Boolean(invalid)}
          {...rest}
        />
      )}

      {error ? (
        <p className="auth__hint" role="alert">
          <bdi>{error}</bdi>
        </p>
      ) : hint ? (
        <p className="auth__note">
          <bdi>{hint}</bdi>
        </p>
      ) : null}
    </div>
  )
}
